import { Injectable, inject } from '@angular/core';
import { ILocationService, IQService, IWindowService } from 'angular';
import { LOCATION_PORT, Q_PORT, WINDOW_PORT, UtilsService } from '../utils';

/**
 * HelpersService (Angular 18)
 *
 * Replaces: apps/b2b-ui/common/services/helpers/helpers.service.js
 * Provides utility methods for string/number conversions, date formatting, color contrast, etc.
 */
@Injectable({ providedIn: 'root' })
export class HelpersService {
  private utils = inject(UtilsService);
  private location = inject<ILocationService>(LOCATION_PORT);
  private $q = inject<IQService>(Q_PORT);
  private $window = inject<IWindowService>(WINDOW_PORT);

  // ─── Static properties (constants) ─────────────────────────────────────────
  readonly errors: unknown[] = [];

  readonly fieldTypes = ['NumberType', 'NumberArrayType', 'StringType', 'StringArrayType', 'RangeType'] as const;

  readonly dateFormats = {
    tinyDate: 'M/d',
    shortDate: 'M/d/yy',
    shortDateAlt: 'MM/DD/YY',
    shortDateTime: "M/d/yy 'at' h:mm a",
    midDate: 'M/d/yyyy',
    midDateAlt: 'M/d/YYYY',
    standardDate: 'MMM d, yyyy',
    standardDateAlt: 'MMM D, YYYY',
    standardTime: 'h:mm a',
    militaryTime: 'HH:mm',
    standardDateTime: "MMM d, yyyy 'at' h:mm a",
    mediumDate: 'EEEE, MMMM d',
    extendedDate: 'EEEE, MMMM d, yyyy',
    extendedDateTime: "EEEE, MMMM d, yyyy 'at' h:mm a",
    sortDate: 'yyyy-M-d',
    sortDateTime: "yyyy-M-d 'at' h:mm a",
    sortDateAlt: 'yyyy/M/d',
    sortDateAlt2: 'yyyy-M-d',
    sortDateTimeAlt: "yyyy/M/d 'at' h:mm a",
    fieldDate: 'yyyy-MM-dd',
    ccExpirationDate: 'MM/YYYY',
    ccExpirationDateAlt: 'MM/yyyy',
    longYear: 'yyyy',
    shortYear: "'yy",
    month: 'MMMM yyyy',
    day: 'dddd, MMM d, yyyy',
    fullCalendarTime: 'h:mmtt',
    week: "'MMM d[ yyyy]{ '&#8212;'[ MMM] d yyyy}'",
    ISO8601: 'yyyy-MM-ddTHH:mm:ssZ',
    momentISO8601: 'YYYY-MM-DDTHH:mm:ssZZ',
  };

  // ─── Public methods ───────────────────────────────────────────────────────

  /**
   * Calculate contrast based on hex color
   * @source https://24ways.org/2010/calculating-color-contrast/
   */
  getContrastYIQ(hexcolor: string): string {
    let hex = hexcolor;
    if (/^#/.test(hexcolor)) {
      const shex = hexcolor.split('');
      shex.shift();
      hex = shex.join('');
    }

    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const yiq = (r * 299 + g * 587 + b * 114) / 1000;

    return yiq >= 128 ? '#000000' : '#ffffff';
  }

  /**
   * Convert a string to array based on delimiter
   */
  stringToArray(value: string, delimiter?: string): string[] {
    const delim = delimiter || ',';
    if (typeof value === 'string' && value.length > 0) {
      return value.split(delim);
    }
    return [];
  }

  /**
   * Convert string or number to integer (currency cents)
   * @deprecated Use @acme/b2b/pricing
   */
  currencyFloatToInteger(value: string | number, significant?: number): number {
    let r = typeof value === 'string' ? parseFloat(value) : value;
    const sigs = significant || 2;

    if (isNaN(r)) {
      return 0;
    }

    const decimal = r * Math.pow(10, sigs);
    let result = parseInt(decimal.toString(), 10);
    const round = decimal.toString().split('.')[1];

    if (round) {
      const digit = parseInt(round.charAt(0), 10);
      if (digit >= 5) {
        result += 1;
      }
    }

    return result;
  }

  /**
   * Convert integer to currency float
   * @deprecated Use @acme/b2b/pricing
   */
  integerToCurrencyFloat(value: number, significant?: number, enforceFixed?: boolean): number | string {
    const sigs = significant || 2;
    let r = value / Math.pow(10, sigs);

    if (isNaN(r)) {
      return 0;
    }

    const round = r.toString().split('.')[1];
    if (round && round.length > 2) {
      const digit = parseInt(round.charAt(2), 10);
      if (digit >= 5) {
        r += 1;
      }
    }

    return enforceFixed ? r.toFixed(2) : r;
  }

  /**
   * Validates date in ISO_8601 format
   */
  validISODate(value: string): boolean {
    const m = (window as any).moment;
    return m ? m(value, m.ISO_8601, true).isValid() : /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value);
  }

  /**
   * Extends an object (shallow merge)
   */
  decorate<A extends object, B extends object>(a: A, b: B): A & B {
    return Object.assign(a, b) as A & B;
  }

  /**
   * Capitalize first character
   */
  cap(s: string): string {
    return s ? s.charAt(0).toUpperCase() + s.slice(1) : '';
  }

  /**
   * Lowercase first character
   */
  low(s: string): string {
    return s ? s.charAt(0).toLowerCase() + s.slice(1) : '';
  }

  /**
   * Capitalize first char, lowercase the rest
   */
  capFirstLetter(s: string): string {
    return s ? s.charAt(0).toUpperCase() + s.substr(1).toLowerCase() : '';
  }

  /**
   * Split camelCase strings into space-separated words
   */
  camelCaseSplit(s: string): string[] {
    const re = /([A-Za-z]?)([a-z]+)/g;
    const output: string[] = [];
    let match = re.exec(s);
    while (match) {
      output.push([match[1].toUpperCase(), match[2]].join(''));
      match = re.exec(s);
    }
    return output;
  }

  /**
   * Merges two arrays uniquely
   */
  unique<T>(a: T | T[], b: T | T[]): T[] {
    const _a: T[] = a ? (Array.isArray(a) ? a : [a]) : [];
    const _b: T[] = b ? (Array.isArray(b) ? b : [b]) : [];
    for (let i = 0, ii = _b.length; i < ii; i++) {
      if (_a.indexOf(_b[i]) === -1) {
        _a.push(_b[i]);
      }
    }
    return _a;
  }

  /**
   * Strip HTML from text
   */
  strip(text: unknown, limit?: number): string {
    let r = '';
    if (text) {
      r = String(text).replace(/<[^>]+>|(?!<br>)(?!&nbsp;)(?!&amp;)&.*?;/gm, '');
      r = r.replace(/<br>/gm, ' ');
      r = r.replace(/&amp;/gm, '&');
      r = r.replace(/&nbsp;/gm, ' ');
      if (limit && r.length > limit) {
        r = r.substring(0, limit - 2) + '...';
      }
    }
    return r;
  }

  /**
   * Check if object is empty (no enumerable properties)
   */
  isEmptyObject(obj: any): boolean {
    if (!obj || typeof obj !== 'object') {
      return true;
    }
    for (const _ in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, _)) {
        return false;
      }
    }
    return true;
  }

  /**
   * Replaces incorrect originalPath from uploaded images with correct path
   */
  correctORImage(path: string): string {
    const regex = /(.*)\/(.*?)_OR\.(png|jpg|gif|jpeg|tiff|bmp)$/;
    return regex.test(path) ? path.replace(regex, '$1/_OR_$2.$3') : path;
  }

  getPaginator(props?: { page?: number; pageSize?: number; sortDirection?: string; sortField?: string }) {
    return {
      page: props?.page || 1,
      pageSize: props?.pageSize || -1,
      sortDirection: props?.sortDirection || 'asc',
      sortField: props?.sortField || 'name',
    };
  }

  getEntityEmptyImage(type: string): string {
    let uuid = '';
    switch (type) {
      case 'facilities':
        uuid = '5afa7c3a-e2ea-4d73-8d53-0e0788a84076';
        break;
      case 'personnel':
        uuid = '09728cd8-2be9-4966-8ca3-7cfe0ef9e5c9';
        break;
      case 'equipment':
        uuid = '21583c37-a424-4f27-b3b6-fdc3bdb437bc';
        break;
    }
    return '/v2/api/photos/' + uuid + '/PM';
  }

  getEntityName(type?: string): {
    singular: string;
    plural: string;
    displaySingular?: string;
    displayPlural?: string;
  } {
    const entityName: { singular: string; plural: string; displaySingular?: string; displayPlural?: string } = {
      singular: '',
      plural: '',
    };
    if (type) {
      switch (type.toLowerCase()) {
        case 'personnel':
          entityName.singular = type;
          entityName.plural = type;
          entityName.displaySingular = 'person';
          entityName.displayPlural = 'people';
          break;
        case 'equipment':
          entityName.singular = type;
          entityName.plural = type;
          entityName.displaySingular = 'equipment';
          entityName.displayPlural = 'equipment';
          break;
        case 'facilities':
          entityName.singular = 'facility';
          entityName.plural = type;
          entityName.displaySingular = 'place';
          entityName.displayPlural = 'places';
          break;
      }
    }
    return entityName;
  }

  /** Return length of input strings (newlines/whitespace normalized) */
  getTextLength(text: unknown): number {
    if (text && typeof text === 'string') {
      return text.replace(/[\s\n]/g, ' ').length;
    }
    return 0;
  }

  /**
   * Return ordinal-suffixed numeric value (1st, 2nd, 3rd, 4th, ...)
   */
  getEth(n: number | string, suffixOnly?: boolean): string {
    const s = String(n);
    const fullNum = parseInt(s, 10);
    const num = parseInt(s.substr(s.length - 1), 10);
    let suffix = 'th';
    switch (num) {
      case 1:
        suffix = 'st';
        break;
      case 2:
        suffix = 'nd';
        break;
      case 3:
        suffix = 'rd';
        break;
      default:
        suffix = 'th';
    }
    if (fullNum === 11 || fullNum === 12 || fullNum === 13) {
      suffix = 'th';
    }
    if (fullNum === 0) {
      suffix = '';
    }
    return suffixOnly ? suffix : s + suffix;
  }

  /** Wrapper for Cookies.get(...) (js-cookie via $window) */
  getCookie(name: string): unknown {
    const Cookies = (this.$window as unknown as { Cookies?: { get(n: string): string | undefined } }).Cookies;
    let cookie: unknown = Cookies?.get(name);
    if (typeof cookie === 'string' && /^\{/.test(cookie)) {
      try {
        cookie = JSON.parse(cookie);
      } catch {
        // leave as string
      }
    }
    return cookie;
  }

  /** Wrapper for Cookies.set(...) (js-cookie via $window) */
  setCookie(name: string, value: unknown): void {
    const Cookies = (
      this.$window as unknown as {
        Cookies?: { set(n: string, v: string, opts?: Record<string, unknown>): void };
      }
    ).Cookies;
    Cookies?.set(name, JSON.stringify(value), { expires: 1, path: '/' });
  }

  /** Set caret position in input/textarea */
  setCaretPosition(elem: HTMLInputElement | HTMLTextAreaElement | null, caretPos: number): void {
    if (elem != null) {
      const ie = elem as unknown as { createTextRange?: () => { move(unit: string, n: number): void; select(): void } };
      if (typeof ie.createTextRange === 'function') {
        const range = ie.createTextRange();
        range.move('character', caretPos);
        range.select();
      } else if (elem.selectionStart != null) {
        elem.focus();
        elem.setSelectionRange(caretPos, caretPos);
      } else {
        elem.focus();
      }
    }
  }

  /** Get caret position of input/textarea */
  getCaretPosition(oField: HTMLInputElement | HTMLTextAreaElement): number {
    let iCaretPos = 0;
    const doc = document as unknown as {
      selection?: { createRange(): { moveStart(u: string, n: number): void; text: string } };
    };
    if (doc.selection) {
      oField.focus();
      const oSel = doc.selection.createRange();
      oSel.moveStart('character', -oField.value.length);
      iCaretPos = oSel.text.length;
    } else if (oField.selectionStart || oField.selectionStart === 0) {
      iCaretPos = oField.selectionStart;
    }
    return iCaretPos;
  }

  /**
   * Select and scroll to a specific line within a textarea
   */
  selectAndScrollToTextareaLine(textarea: HTMLTextAreaElement, lineNum: number): boolean {
    lineNum--;
    const lines = textarea.value.split('\n');
    const lineHeight = parseInt(getComputedStyle(textarea).lineHeight, 10);
    let startPos = 0;
    let endPos = textarea.value.length;
    for (let x = 0; x < lines.length; x++) {
      if (x === lineNum) {
        break;
      }
      startPos += lines[x].length + 1;
    }
    endPos = lines[lineNum].length + startPos;

    if (typeof textarea.selectionStart !== 'undefined') {
      textarea.focus();
      textarea.selectionStart = startPos;
      textarea.selectionEnd = endPos;
      textarea.scrollTop = lineNum * lineHeight;
      return true;
    }
    return false;
  }

  /**
   * Gets the value of a field based on its type
   */
  getFieldValue(field: any, stringify?: boolean): unknown {
    if (Object.prototype.hasOwnProperty.call(field, 'type')) {
      const fieldKey = this.low(String(field.type).replace('Type', 'Value'));
      if (field[fieldKey]) {
        if (fieldKey.indexOf('Array') > 0 && stringify) {
          return field[fieldKey].join(',');
        }
        return field[fieldKey];
      }
    }
    return undefined;
  }

  /**
   * Gets field type and simpleType
   */
  getFieldType(field: any): { type: string; simpleType: string } | false {
    if (Object.prototype.hasOwnProperty.call(field, 'type')) {
      return {
        type: field.type,
        simpleType: this.low(String(field.type).replace('Type', '').replace('Array', '')),
      };
    }
    return false;
  }

  /**
   * Parse a value into one of: NumberType, NumberArrayType, StringType, StringArrayType, RangeType
   */
  parseValueType(type: 'number' | 'string' | 'range' | string, value: unknown, allowEmptyValues?: boolean): any {
    let r: any = false;
    let valueSimpleType: string | undefined;
    let errorMessage: string | undefined;
    let isArray = typeof value === 'string' ? /,/g.test(value) : Array.isArray(value);
    const arrayDelim = ',';
    const rangeDelim = '-';

    if (value) {
      switch (type) {
        case 'number':
          valueSimpleType = type;
          if (isArray) {
            r = Array.isArray(value) ? (value as unknown[]).slice() : String(value).split(arrayDelim);
            for (let i = 0, ii = r.length; i < ii; i++) {
              if (isNaN(r[i])) {
                r = false;
                errorMessage = 'Only numeric values allowed';
                break;
              } else {
                r[i] = parseInt(r[i], 10);
              }
            }
          } else if (!isNaN(value as number)) {
            r = parseInt(String(value), 10);
          } else {
            errorMessage = 'Only numeric values allowed';
          }
          break;
        case 'string':
          valueSimpleType = type;
          if (isArray) {
            r = Array.isArray(value) ? (value as unknown[]).slice() : String(value).split(arrayDelim);
            for (let i = 0, ii = r.length; i < ii; i++) {
              if (typeof r[i] === 'string') {
                r[i] = r[i].replace(/^\s*([\w\s.]*)\s*$/, '$1');
              }
            }
          } else {
            r = value;
          }
          break;
        case 'range': {
          valueSimpleType = type;
          let range: string[];
          let rangeDefault: string;
          if (isArray) {
            rangeDefault = String(value).split(arrayDelim)[1];
            range = String(value).split(arrayDelim)[0].split(rangeDelim);
          } else {
            range = String(value).split(rangeDelim);
            rangeDefault = range[0];
          }
          isArray = false;
          if (isNaN(Number(range[0])) || isNaN(Number(range[1])) || isNaN(Number(rangeDefault))) {
            r = false;
            errorMessage = 'Only numeric values allowed';
            break;
          }
          if (range.length === 2) {
            r = {
              rangeValue: parseInt(rangeDefault, 10),
              rangeMinValue: parseInt(range[0], 10),
              rangeMaxValue: parseInt(range[1], 10),
            };
          } else {
            errorMessage = 'Invalid range';
            r = false;
          }
          break;
        }
        default:
          errorMessage = 'Invalid type';
          r = false;
      }
    } else if (!value && allowEmptyValues) {
      switch (type) {
        case 'number':
        case 'string':
        case 'range':
          valueSimpleType = type;
          r = null;
          break;
        default:
          errorMessage = 'Invalid type';
          r = false;
      }
    } else {
      errorMessage = 'No Value';
    }

    if (r !== false) {
      const valueType = this.cap(type) + (isArray ? 'Array' : '') + 'Type';
      const keyType = type + (isArray ? 'Array' : '') + 'Value';
      return { type: valueType, key: keyType, simpleType: valueSimpleType, value: r };
    }

    (this as any).errors = [{ message: errorMessage }];
    return r;
  }

  /**
   * Returns the value of an item's custom field by name
   */
  getCustomFieldValue(item: any, name: string): unknown {
    if (
      item &&
      Object.prototype.hasOwnProperty.call(item, 'customFields') &&
      Array.isArray(item.customFields) &&
      name
    ) {
      for (let i = 0, ii = item.customFields.length; i < ii; i++) {
        const f = item.customFields[i];
        if (f && Object.prototype.hasOwnProperty.call(f, 'name') && f.name === name) {
          return f.value;
        }
      }
    }
    return '';
  }

  /**
   * Converts months into years/months readable string
   */
  getFormattedDuration(durationInMonths: number | string): string {
    let r = '';
    let years = 0;
    let months = 0;
    const months_n = parseInt(String(durationInMonths), 10);
    if (durationInMonths != null && !isNaN(months_n)) {
      if (months_n >= 12) {
        years = parseInt(String(months_n / 12), 10);
        months = months_n % 12;
      } else {
        months = months_n;
      }
      if (years) {
        r = years + (years === 1 ? ' Year' : ' Years');
      }
      if (months) {
        r += (r ? ', ' : '') + (months + (months === 1 ? ' Month' : ' Months'));
      }
    }
    return r;
  }

  /**
   * Index an array by a predicate-derived key
   */
  keyBy<T>(
    values: T[] | null | undefined,
    predicateFn: (item: T, index: number) => string | number,
  ): Record<string, T> {
    const arr = values || [];
    return arr.reduce<Record<string, T>>((acc, item, index) => {
      const key = predicateFn(item, index);
      acc[key as string] = item;
      return acc;
    }, {});
  }

  /**
   * Creates an entities object with ids array + entities dictionary
   */
  createEntityState<T>(
    values: T[] | null | undefined,
    predicateFn: (item: T, index: number) => string | number,
  ): { ids: Array<string | number>; entities: Record<string, T> } {
    const arr = values || [];
    const ids = arr.map(predicateFn);
    const entities = this.keyBy(arr, predicateFn);
    return { ids, entities };
  }

  /**
   * Retrieves a value at a given path, returning defaultValue if not found
   */
  path(nestedObj: any, p: Array<string | number>, defaultValue?: unknown): unknown {
    const value = p.reduce<any>((obj, key) => {
      return obj && obj[key] !== 'undefined' ? obj[key] : undefined;
    }, nestedObj);
    return value == null ? defaultValue : value;
  }

  /**
   * Coerces a value to a boolean (treats string "false" as false, null/undefined as false)
   */
  coerceBooleanProperty(value: unknown): boolean {
    return value != null && String(value) !== 'false';
  }

  /**
   * Moves an item in an array from one index to another (in-place)
   */
  moveItemInArray<T>(array: T[], fromIndex: number, toIndex: number): void {
    const clamp = (n: number, min: number, max: number) => Math.min(Math.max(n, min), max);
    const from = clamp(fromIndex, 0, array.length - 1);
    const to = clamp(toIndex, 0, array.length - 1);
    if (from === to) {
      return;
    }
    const target = array[from];
    const delta = to < from ? -1 : 1;
    for (let i = from; i !== to; i += delta) {
      array[i] = array[i + delta];
    }
    array[to] = target;
  }
}
